import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/AuthContext";
import LogoMark from "@/components/LogoMark";
import { usePage } from "@/lib/usePage";

const NavLinks = ({ items, onClick }) =>
  items.map((i) => (
    <Link
      key={i.to}
      to={i.to}
      onClick={onClick}
      data-testid={`nav-${i.to.replace("/", "")}`}
      className="link-underline text-sm uppercase tracking-[0.15em] text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors"
    >
      {i.label}
    </Link>
  ));

function Header() {
  const { t, lang, toggle } = useI18n();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const { content: n } = usePage("nav", {});
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = [
    { to: "/expeditions", label: n.expeditions || t("nav_expeditions") },
    { to: "/histoire", label: n.story || t("nav_story") },
    { to: "/contact", label: n.contact || t("nav_contact") },
  ];
  const ctaLabel = n.cta_book || t("cta_book");

  return (
    <header
      className={`fixed top-0 w-full z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-[#0A0A0A]/90 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-home" className="flex items-center gap-2.5 font-clash font-bold text-xl tracking-tight uppercase">
          <LogoMark className="h-8 w-auto" />
          <span>Hors<span className="text-[#BC5D2E]">Signal</span></span>
        </Link>
        <nav className="hidden lg:flex items-center gap-8">
          <NavLinks items={items} />
        </nav>
        <div className="hidden lg:flex items-center gap-6">
          <button data-testid="lang-toggle" onClick={toggle} className="flex items-center gap-1 text-sm uppercase tracking-widest text-[#A3A3A3] hover:text-white">
            <Globe size={14} /> {lang}
          </button>
          {user && (
            <div className="flex items-center gap-4">
              <span data-testid="user-name" className="text-sm uppercase tracking-widest text-[#BC5D2E]">{user.name.split(" ")[0]}</span>
              <button data-testid="logout-btn" onClick={logout} className="text-sm uppercase tracking-widest text-[#A3A3A3] hover:text-white">{t("logout")}</button>
            </div>
          )}
          <button
            data-testid="header-cta"
            onClick={() => nav("/expeditions")}
            className="bg-[#BC5D2E] text-white px-6 py-3 uppercase tracking-widest text-xs hover:bg-[#8A281E] transition-colors"
          >
            {ctaLabel}
          </button>
        </div>
        <button data-testid="mobile-menu-btn" className="lg:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-[#0A0A0A] border-t border-white/10 px-6 py-8 flex flex-col gap-6">
          <NavLinks items={items} onClick={() => setOpen(false)} />
          <div className="flex items-center gap-6 pt-4 border-t border-white/10">
            <button onClick={toggle} className="flex items-center gap-1 text-sm uppercase tracking-widest text-[#A3A3A3]"><Globe size={14} /> {lang}</button>
            {user && (
              <button onClick={() => { logout(); setOpen(false); }} className="text-sm uppercase tracking-widest text-[#A3A3A3]">{t("logout")}</button>
            )}
            <button onClick={() => { nav("/expeditions"); setOpen(false); }} className="bg-[#BC5D2E] text-white px-6 py-3 uppercase tracking-widest text-xs">{ctaLabel}</button>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { t } = useI18n();
  const { content: f } = usePage("footer", {});
  const email = f.email || "contact@horssignal.com";
  const [nlEmail, setNlEmail] = useState("");
  const subscribe = async (e) => {
    e.preventDefault();
    if (!nlEmail) return;
    try { await api.post("/newsletter", { email: nlEmail }); toast.success(t("subscribed_ok")); setNlEmail(""); }
    catch { toast.error(t("subscribe_err")); }
  };
  return (
    <footer data-testid="footer" className="border-t border-white/10 bg-[#0A0A0A] relative z-10">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 py-16 grid md:grid-cols-3 gap-12">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <LogoMark className="h-10 w-auto" />
            <span className="font-clash font-bold text-2xl uppercase">Hors<span className="text-[#BC5D2E]">Signal</span></span>
          </div>
          <p className="text-[#737373] text-sm max-w-xs">{f.tagline || t("footer_tag")}</p>
          <form onSubmit={subscribe} className="mt-6 max-w-xs" data-testid="footer-newsletter">
            <span className="font-mono-hs text-[10px] uppercase tracking-widest text-[#737373]">{t("newsletter_title")}</span>
            <div className="mt-2 flex items-center border border-white/15 focus-within:border-[#BC5D2E] transition-colors">
              <input type="email" required value={nlEmail} onChange={(e) => setNlEmail(e.target.value)} placeholder={t("email_placeholder")} className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm text-[#F5F5F0] placeholder:text-[#555] outline-none" data-testid="footer-newsletter-input" />
              <button type="submit" aria-label={t("subscribe")} className="px-3 py-2.5 text-[#BC5D2E] hover:bg-[#BC5D2E] hover:text-white transition-colors" data-testid="footer-newsletter-submit"><ArrowRight size={16} /></button>
            </div>
          </form>
          <div className="flex flex-col gap-3 mt-6">
            <a href={`mailto:${email}`} className="text-sm text-[#A3A3A3] hover:text-white" data-testid="footer-email">{email}</a>
            <a href={f.instagram_url || "https://instagram.com/wearehorssignal"} target="_blank" rel="noreferrer" className="text-sm text-[#A3A3A3] hover:text-white uppercase tracking-widest w-fit" data-testid="footer-instagram">{f.instagram_label || "Instagram"}</a>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <Link to="/expeditions" className="text-sm uppercase tracking-widest text-[#A3A3A3] hover:text-white">{f.col_expeditions || t("nav_expeditions")}</Link>
          <Link to="/histoire" className="text-sm uppercase tracking-widest text-[#A3A3A3] hover:text-white">{f.col_story || t("nav_story")}</Link>
          <Link to="/contact" className="text-sm uppercase tracking-widest text-[#A3A3A3] hover:text-white">{f.col_contact || t("nav_contact")}</Link>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Link data-testid="footer-mentions" to="/mentions-legales" className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#BC5D2E]">{f.legal_mentions || t("legal_mentions")}</Link>
            <Link data-testid="footer-cgv" to="/cgv" className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#BC5D2E]">{f.legal_cgv || t("legal_cgv")}</Link>
            <Link data-testid="footer-privacy" to="/confidentialite" className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#BC5D2E]">{f.legal_privacy || t("legal_privacy")}</Link>
            <Link data-testid="footer-cookies" to="/cookies" className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#BC5D2E]">{f.legal_cookies || t("legal_cookies")}</Link>
            <button data-testid="footer-cookie-settings" onClick={() => window.dispatchEvent(new Event("hs-open-cookies"))} className="text-left text-xs uppercase tracking-widest text-[#737373] hover:text-[#BC5D2E]">{f.cookie_settings || t("cookie_settings")}</button>
          </div>
        </div>
      </div>
      <div className="border-t border-white/10 py-6 text-center">
        <p className="text-[#737373] text-xs">{f.copyright || `© ${new Date().getFullYear()} HorsSignal — Tous droits réservés.`}</p>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
